import {
  BackEndValueType,
  CommonHelperFunctions,
  EPropertyType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IComponent,
  IDesign,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IProject,
  IPropertyDefinition,
  IScreenDesign,
  ISymbol,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import { collectSettableValueProps, ISettableValueProp } from "../../../../shared/utility/settable-value-props";
import KuikaSelectbox, {
  KuikaSelectboxOption,
  KuikaSelectboxOptionGroup
} from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

const Pagination = "Pagination";
const paginationValuePropName = "current";
const paginationPageSizeValuePropName = "pageSize";

interface IEventTypeOption {
  label: string;
  value: string;
}

export interface ISetValueSymbolsGroupState {
  selectedDI: IDisplayItem;
}

export interface ISetValueSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
}

class SetValueSymbolsGroup extends PureSdComponent<
  ISetValueSymbolsGroupProps & IWithSDProps & IWithContextProp,
  ISetValueSymbolsGroupState
> {
  propertySdHelper: PropertySdHelper;

  constructor(props: ISetValueSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      selectedDI: undefined as unknown as IDisplayItem
    };
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
  }

  componentDidMount() {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    let allDi: IDisplayItem[] = [];
    this.fillAllDisplayItemsRecursive(sd, allDi);
    allDi = this.filterDisplayItemsByValueProperty(allDi);
    const initialSelectValue = this.getSelectValue(allDi);
    this.setState({ selectedDI: allDi[initialSelectValue] });
  }

  getScreenDesignContextProviderValues = () => {
    return this.props.sd;
  };

  handleSelectSymbolPickerValue = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const valueType = this.getValueType();
    symbol.targetType =
      parameter.name === "triggerEvents" ? "Boolean" : parameter.name === "eventType" ? "String" : valueType;
    parameter.Typename =
      parameter.name === "triggerEvents" ? "Boolean" : parameter.name === "eventType" ? "String" : valueType;
    const params = _.cloneDeep(this.props.parameters);
    const _parameter = parameter;
    _parameter.symbol = symbol;
    const valuesToUpdate = {
      actionParameters: params.filter((actionParameter) => actionParameter.name !== parameter.name)
    } as IActionPanelStatesCanBeUpdatedFromChild;
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  getValueType = (): BackEndValueType => {
    const multipleValuePropNameParameter = this.props.parameters.find(
      (element) => element.name === "multipleValuePropName"
    );
    const { item } = this.state.selectedDI;
    const selectedName: string | undefined = multipleValuePropNameParameter?.symbol?.fixedValue;
    const valueProps: ISettableValueProp[] = this.getSettableValueProps(item, selectedName);
    const selectedValueProp: ISettableValueProp | undefined = valueProps.find((p) => p.name === selectedName);

    let valueProp: ISettableValueProp | undefined;
    if (valueProps.length === 1) valueProp = valueProps[0];
    else if (selectedValueProp && multipleValuePropNameParameter) {
      valueProp = selectedValueProp;
    }
    const valueType = this.backEndValueToEPropertyType(valueProp?.selectedType!);

    return valueType;
  };

  /**
   * The value props SetValueOf may target on `item`, read from the component definition so an
   * instance that predates a value prop still offers it and a deprecated one is hidden (KOA-18575).
   */
  getSettableValueProps = (
    item: IDisplayItemComponent | undefined,
    currentlySelected: string | undefined
  ): ISettableValueProp[] => {
    const definition: IComponent | undefined = item
      ? CommonHelperFunctions.findComponentById(item.id, this.props.sd.project?.componentSetObj?.components ?? [])
      : undefined;
    return collectSettableValueProps(item, definition, currentlySelected);
  };

  handleComponentSelect = (value: any, allDi: IDisplayItem[], allMasterDi: IDisplayItem[]) => {
    let fixedValue = allDi[value]?.id;
    let diItem = allDi[value]?.item;
    // if (typeof value === "string" && value?.includes("_master")) {
    //   fixedValue = parseInt(allMasterDi[value.replace("_master", "")]?.id);
    //   diItem = allMasterDi[value.replace("_master", "")]?.item;
    //   this.setState({ selectedDI: allMasterDi[value.replace("_master", "")] });
    // } else {
    this.setState({ selectedDI: allDi[value] });
    // }
    const params = _.cloneDeep(this.props.parameters);
    const _parameter = this.props.parameters.find((element) => element.name === "targetDiName")!;
    if (_parameter === undefined) {
      return;
    }
    if (!_parameter.symbol) _parameter.symbol = {} as any;
    _parameter.symbol.fixedValue = fixedValue;
    _parameter.symbol.category = ESymbolCategory.fixed;
    let valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;

    const multipleValuePropNameParam = _.clone(this.props.parameters.find((x) => x.name === "multipleValuePropName"));
    const valueProps: ISettableValueProp[] = this.getSettableValueProps(diItem, undefined);
    if (diItem?.name === Pagination) {
      diItem.properties?.forEach((element) => {
        const propertyDefinition: IPropertyDefinition | undefined = this.getPropertyDefinition(
          diItem,
          element,
          this.props.sd.project
        );
        if (
          (propertyDefinition?.name == paginationValuePropName ||
            propertyDefinition?.name == paginationPageSizeValuePropName) &&
          !valueProps.some((p) => p.name === element.name)
        ) {
          valueProps.push({ name: element.name, selectedType: element.selectedType, tagName: "" });
        }
      });
    }

    if (valueProps.length <= 1 && multipleValuePropNameParam) {
      multipleValuePropNameParam.symbol = {} as ISymbol;
      valuesToUpdate.actionParameters = valuesToUpdate.actionParameters?.filter(
        (p) => p.name !== multipleValuePropNameParam.name
      );
      valuesToUpdate.actionParameters?.push(multipleValuePropNameParam);
    }

    valuesToUpdate.actionParameters = valuesToUpdate.actionParameters?.filter((p) => p.name !== _parameter.name);
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate);
  };

  handleMultipleValuePropSelect = (value: string) => {
    const params = _.cloneDeep(this.props.parameters);
    const _parameter = this.props.parameters.find((element) => element.name === "multipleValuePropName")!;
    if (_parameter === undefined) {
      return;
    }
    if (!_parameter.symbol) _parameter.symbol = {} as any;
    _parameter.symbol.fixedValue = value;
    _parameter.symbol.category = ESymbolCategory.fixed;
    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate);
  };

  getSelectValue = (allDi: IDisplayItem[]) => {
    let selectValue;
    const _parameter = this.props.parameters.find((element) => element.name === "targetDiName")!;
    if (_parameter && _parameter.symbol) {
      const { fixedValue } = _parameter.symbol;
      allDi.forEach((di, i) => {
        if (di.id.toString() === fixedValue.toString()) {
          selectValue = i;
        }
      });
    }
    return selectValue;
  };

  filterDisplayItemsByValueProperty = (allDi: IDisplayItem[]): IDisplayItem[] => {
    const newDiList = [] as IDisplayItem[];
    allDi.forEach((di) => {
      const { item } = di;
      let isAdded = false;
      item.properties.forEach((element) => {
        const propertyDefinition: IPropertyDefinition | undefined = this.getPropertyDefinition(
          item,
          element,
          this.props.sd.project
        );
        if ((propertyDefinition?.isValueProp || di.item.name === Pagination) && isAdded == false) {
          newDiList.push(di);
          isAdded = true;
        }
      });
    });
    return newDiList;
  };

  backEndValueToEPropertyType = (selectedType: EPropertyType): BackEndValueType => {
    switch (selectedType) {
      case EPropertyType.colorPicker:
      case EPropertyType.string:
        return "String";
      case EPropertyType.number:
        return "Decimal";
      case EPropertyType.image:
        return "Image";
      case EPropertyType.dateTime:
        return "DateTimeOffset";
      case EPropertyType.boolean:
        return "Boolean";
      default:
        return "Unknown";
    }
  };

  getPropertyDefinition = (
    diComponent: IDisplayItemComponent,
    diProperty: IDisplayItemProperty,
    project: IProject
  ): IPropertyDefinition | undefined => {
    if (project.componentSetObj && project.componentSetObj.components) {
      const component: IComponent | undefined = CommonHelperFunctions.findComponentById(
        diComponent?.id,
        project.componentSetObj.components
      );
      if (component) {
        const property = component.properties.find((p) => p.name === diProperty.name);
        if (property) {
          const result: IPropertyDefinition = {
            description: property.description,
            hasOptions: property.hasOptions,
            isRequired: property.isRequired,
            isTechnical: property.isTechnical,
            // isEvent: property.isEvent,
            isVisibleInFieldPicker: property.isVisibleInFieldPicker,
            isValueProp: property.isValueProp,
            isOptionsProp: property.isOptionsProp,
            isLanguageOptionsProp: property.isLanguageOptionsProp,
            isEditabilityProp: property.isEditabilityProp,
            isVisibleProp: property.isVisibleProp,
            isAdvanced: property.isAdvanced,
            isIgnoredOnCodeOutput: property.isIgnoredOnCodeOutput,
            isIgnoredOnGeneratedApp: property.isIgnoredOnGeneratedApp,
            isSupportMultiLanguage: property.isSupportMultiLanguage,
            appSupportType: property.appSupportType,
            isDeprecated: property.isDeprecated,
            name: property.name,
            options: property.options,
            tagName: property.tagName,
            type: property.type,
            typeOptions: property.typeOptions,
            defaultValue: property.symbol?.category === ESymbolCategory.fixed ? property.symbol.fixedValue : "",
            isChartOptionsProp: property.isChartOptionsProp,
            isTimelineOptionsProp: property.isTimelineOptionsProp,
            isChatOptionsProp: property.isChatOptionsProp,
            isCalendarOptionsProp: property.isCalendarOptionsProp,
            isMenuOptionsProp: property.isMenuOptionsProp,
            isBreadcrumbOptionsProp: property.isBreadcrumbOptionsProp,
            isRadialChartOptionsProp: property.isRadialChartOptionsProp,
            isRadialChartLevelOptionsProp: property.isRadialChartLevelOptionsProp,
            isMixedChartOptionsProp: property.isMixedChartOptionsProp,
            isKanbanBoardCardOptionsProp: property.isKanbanBoardCardOptionsProp,
            isKanbanBoardLaneOptionsProp: property.isKanbanBoardLaneOptionsProp,
            isNodeTreeNodeOptionsProp: property.isNodeTreeNodeOptionsProp,
            isMixedChartLineOptionsProp: property.isMixedChartLineOptionsProp,
            isMixedChartPointOptionsProp: property.isMixedChartPointOptionsProp,
            isMixedChartBarOptionsProp: property.isMixedChartBarOptionsProp,
            isTreeOptionsProp: property.isTreeOptionsProp,
            inVisibleStyles: property.inVisibleStyles,
            hideFixedValue: property.hideFixedValue
          };
          return result;
        }
      }
    }
  };

  getTabValue = (): any => {
    const parameter = this.props.parameters.find((element) => element.name === "value");
    if (parameter && parameter.symbol) {
      return parameter?.symbol.fixedValue;
    }
    return undefined;
  };

  getProjectValue = (): any => {
    const parameter = this.props.parameters.find((element) => element.name === "value");
    if (parameter && parameter.symbol) {
      return parameter?.symbol.fixedValue;
    }
    return undefined;
  };

  getPropDef = (kuikaId: string | undefined, diProp: { name: string }) => {
    return this.propertySdHelper.getProperties(kuikaId ?? "").find((p) => p.name === diProp.name);
  };

  tabValueOnChange = (value): any => {
    const symbol = {} as ISymbol;
    symbol.category = ESymbolCategory.fixed;
    symbol.description = "";
    symbol.fixedValue = value;
    symbol.isFixed = true;
    symbol.label = "Fixed Value";
    return symbol;
  };

  projectValueOnChange = (value): any => {
    const symbol = {} as ISymbol;
    symbol.category = ESymbolCategory.fixed;
    symbol.description = "";
    symbol.fixedValue = value;
    symbol.isFixed = true;
    symbol.label = "Fixed Value";
    return symbol;
  };

  renderSymbolPicker = (): JSX.Element => {
    let valueProp: ISettableValueProp | undefined;
    let di;
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
    const targetDiParameter = this.props.parameters.find((element) => element.name === "targetDiName");
    if (targetDiParameter?.symbol && targetDiParameter?.symbol.fixedValue) {
      const targetDiID = targetDiParameter?.symbol.fixedValue;
      if (targetDiID) {
        di = this.props.sd.screenDesignDictionary[parseInt(targetDiID, 10)];
      }
      const parameter = this.props.parameters.find((element) => element.name === "value");

      if (di?.item?.name == "Tab" || di?.item?.name == "tab") {
        const tabOptions: KuikaSelectboxOption[] = di.children.map((child: IDisplayItem): KuikaSelectboxOption => {
          const label: string | undefined = child.friendlyName;
          // const labelProp = child.item.properties.find((element) => element.name === "tab");
          // if (labelProp.symbol.fixedValue) {
          //   label = labelProp.symbol.fixedValue;
          // }
          return { key: child.id.toString(), value: child.id, label };
        });

        return (
          <>
            <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
              Tab to Select
            </div>
            <div className="kuika_property-grid-column">
              <KuikaSelectbox
                searchable
                size="sm"
                style={{ width: "100%" }}
                value={this.getTabValue() ?? null}
                onChange={(val: number): void => {
                  this.handleSelectSymbolPickerValue(parameter!, this.tabValueOnChange(val));
                }}
                className="kuika_add_actions_inputs_size"
                options={tabOptions}
              />
            </div>
          </>
        );
      }

      if (di?.item?.name === "Stepper") {
        const stepOptions: KuikaSelectboxOption[] = di.children.map((child: IDisplayItem): KuikaSelectboxOption => {
          let label: string | undefined = child.friendlyName;
          const labelProp: IDisplayItemProperty | undefined = child.item?.properties?.find(
            (element: IDisplayItemProperty): boolean => element.name === "title"
          );
          if (labelProp?.symbol?.fixedValue) {
            label = labelProp.symbol.fixedValue;
          }
          return { key: child.id.toString(), value: child.id, label };
        });

        return (
          <>
            <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
              Step to Select
            </div>
            <div className="kuika_property-grid-column">
              <KuikaSelectbox
                searchable
                size="sm"
                style={{ width: "100%" }}
                value={this.getTabValue() ?? null}
                onChange={(val: number): void => {
                  this.handleSelectSymbolPickerValue(parameter!, this.tabValueOnChange(val));
                }}
                className="kuika_add_actions_inputs_size"
                options={stepOptions}
              />
            </div>
          </>
        );
      }

      if (di?.item?.name === "MicroApp") {
        // The provider hands out its own project summary shape, so the row type is left to it.
        const projectOptions: KuikaSelectboxOption[] = this.props.sd.projects.map((project): KuikaSelectboxOption => ({
          key: project.id,
          value: project.id,
          label: project.name
        }));

        return (
          <>
            <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
              Project
            </div>
            <div className="kuika_property-grid-column">
              <KuikaSelectbox
                searchable
                size="sm"
                style={{ width: "100%" }}
                value={this.getProjectValue() ?? null}
                onChange={(val: string): void => {
                  this.handleSelectSymbolPickerValue(
                    this.props.action.parameters.find((p) => p.name === "value")!,
                    this.projectValueOnChange(val)
                  );
                }}
                className="kuika_add_actions_inputs_size"
                options={projectOptions}
              />
            </div>
          </>
        );
      }
    }
    let valueParameter = this.props.parameters.find((element) => element.name === "value");
    if (di?.item?.name === "Paginaiton") {
      valueParameter = this.props.parameters.find((element) => element.name === paginationValuePropName);
    }
    const multipleValuePropNameParameter = this.props.parameters.find(
      (element) => element.name === "multipleValuePropName"
    );
    if (this.state.selectedDI && valueParameter) {
      const { item } = this.state.selectedDI;
      const selectedName: string | undefined = multipleValuePropNameParameter?.symbol?.fixedValue;
      const valueProps: ISettableValueProp[] = this.getSettableValueProps(item, selectedName);
      if (di?.item?.name === Pagination) {
        item.properties.forEach((element) => {
          const propertyDefinition: IPropertyDefinition | undefined = this.getPropertyDefinition(
            item,
            element,
            this.props.sd.project
          );
          if (
            (propertyDefinition?.name == paginationValuePropName ||
              propertyDefinition?.name == paginationPageSizeValuePropName) &&
            !valueProps.some((p) => p.name === element.name)
          ) {
            valueProps.push({ name: element.name, selectedType: element.selectedType, tagName: "" });
          }
        });
      }
      const selectedValueProp: ISettableValueProp | undefined = valueProps.find((p) => p.name === selectedName);

      if (valueProps.length === 1) valueProp = valueProps[0];
      else if (selectedValueProp && multipleValuePropNameParameter) {
        valueProp = selectedValueProp;
      }

      const valueType = this.backEndValueToEPropertyType(valueProp?.selectedType!);
      // `collectSettableValueProps` already resolved each prop's display name, so the option
      // takes it from there rather than looking the definition up a second time.
      const valuePropOptions: KuikaSelectboxOption[] = valueProps.map(
        (p: ISettableValueProp): KuikaSelectboxOption => ({
          key: p.name,
          value: p.name,
          label: p.tagName || p.name
        })
      );

      return (
        <>
          {valueProps.length > 1 && (
            <>
              <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
                Prop name to set
              </div>
              <div className="kuika_property-grid-column">
                <KuikaSelectbox
                  size="sm"
                  style={{ width: "100%" }}
                  value={multipleValuePropNameParameter?.symbol?.fixedValue ?? null}
                  onChange={this.handleMultipleValuePropSelect}
                  className="kuika_add_actions_inputs_size"
                  options={valuePropOptions}
                />
              </div>
            </>
          )}
          {(valueProps.length === 1 ||
            (valueProps.length > 1 &&
              multipleValuePropNameParameter?.symbol?.fixedValue &&
              multipleValuePropNameParameter.symbol.fixedValue.length > 0)) && (
            <>
              <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                <ActionParameterTitle
                  parameter={{
                    name: valueParameter.name,
                    Typename: valueType,
                    cardinality: valueParameter.cardinality
                  }}
                />
              </span>
              <SymbolPicker
                action={this.props.action}
                changeType="onBlur"
                // Names this picker's test-ids: `setValueOfSymbolPicker*` on the cascader and
                // `property-input-setValueOf` on the fixed-value box. Without it the picker has no
                // `parameter` or `property` to derive a name from, so every id came out `undefined*`.
                data_testid="setValueOf"
                symbol={valueParameter.symbol}
                valueType={valueType}
                targetCardinality={valueParameter.cardinality}
                targetDI={selectedDi}
                onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                  this.handleSelectSymbolPickerValue(valueParameter!, symbol, changeReason)
                }
                bindingParameter={valueParameter}
                bindingLabel={valueParameter.name}
                parameterOptions={
                  valueProp && this.getPropDef(di?.id, valueProp)?.options?.length
                    ? this.getPropDef(di?.id, valueProp)?.options.map((option) => ({
                        label: option,
                        value: option
                      }))
                    : undefined
                }
                isColorPicker={valueProp?.selectedType === EPropertyType.colorPicker}
              ></SymbolPicker>
            </>
          )}
        </>
      );
    }
    return <></>;
  };

  onMouseEnterComponent = (di: IDisplayItem): void => {
    if (!di || !di.id) return;
    this.props.sd.highlightHoveredItem(di.id.toString());
  };

  renderComponentList = (): JSX.Element => {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    let allDi: IDisplayItem[] = [];
    let allMasterDi: IDisplayItem[] = [];

    this.fillAllDisplayItemsRecursive(sd, allDi);
    allDi = this.filterDisplayItemsByValueProperty(allDi)
      .sort((a, b) => CommonHelperFunctions.compareObjectWithField(a, b, "friendlyName"))
      .filter((di) => {
        return di.item.name != "Button" && di.item.name != "Tree";
      });

    const rootDI = allDi.find((di) => di.id.toString() === SpecialDIIds.rootDI.toString() && di.children.length > 0);
    if (rootDI) {
      rootDI.friendlyName = "Screen";
    }

    // The value a component is picked by is its place in this list, not its id, so the row the
    // pointer is over is read back the same way.
    const componentGroups: KuikaSelectboxOptionGroup[] = [
      {
        label: "Screen",
        options: allDi.map((di: IDisplayItem, index: number): KuikaSelectboxOption => ({
          key: `set-valueof-${this.props.action.id}-component-${index}`,
          value: index,
          label: di.friendlyName,
          testId: `setValueTargetComponentOption-${di.friendlyName}`
        }))
      }
    ];

    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Component to Change
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            data-testid="setValueTargetComponentDropdown"
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={this.getSelectValue(allDi) ?? null}
            onChange={(val: number): void => {
              this.handleComponentSelect(val, allDi, allMasterDi);
            }}
            onOptionMouseEnter={(value: string | number | null): void =>
              this.onMouseEnterComponent(allDi[Number(value)])
            }
            className="kuika_add_actions_inputs_size"
            groups={componentGroups}
          />
        </div>
      </>
    );
  };

  renderTriggerEvents = (): JSX.Element => {
    const triggerEventsParam = this.props.parameters.find((element) => element.name === "triggerEvents");

    if (!triggerEventsParam) return <></>;

    const shouldShowParameter =
      triggerEventsParam.isOptionalParameter === true
        ? triggerEventsParam.symbol?.fixedValue
          ? true
          : triggerEventsParam.isExternalVisible === true
        : true;

    if (!shouldShowParameter) return <></>;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          margin: "12px 0",
          backgroundColor: "var(--dark-0)",
          padding: "12px 0",
          borderRadius: "var(--radius-l)"
        }}
      >
        <div className="kuika_add_actions_properties_name">Trigger Events</div>
        <KuikaSwitch
          checked={triggerEventsParam?.symbol?.fixedValue}
          onChange={(checked: boolean) => {
            const params = _.cloneDeep(this.props.parameters);
            const eventTypeParam = this.props.parameters.find((element) => element.name === "eventType");

            this.handleSelectSymbolPickerValue(triggerEventsParam!, {
              ...triggerEventsParam.symbol,
              category: ESymbolCategory.fixed,
              fixedValue: checked,
              targetType: "Boolean"
            });

            if (checked && eventTypeParam) {
              const eventTypes = this.getEventPropertiesFromComponent();
              const firstEventType = eventTypes.length > 0 && eventTypes[0].value;

              if (firstEventType) {
                const eventTypeSymbol = {
                  ...eventTypeParam.symbol,
                  category: ESymbolCategory.fixed,
                  fixedValue: firstEventType,
                  targetType: "String" as BackEndValueType
                };

                this.handleSelectSymbolPickerValue(eventTypeParam, eventTypeSymbol);
              }
            }
          }}
        />
      </div>
    );
  };

  getEventPropertiesFromComponent = (): IEventTypeOption[] => {
    const targetDiParameter = this.props.parameters.find((element) => element.name === "targetDiName");

    if (!targetDiParameter?.symbol?.fixedValue) {
      return [];
    }

    const targetDiID = targetDiParameter.symbol.fixedValue;
    const di = this.props.sd.screenDesignDictionary[parseInt(targetDiID, 10)];

    if (!di?.item) {
      return [];
    }

    const eventProperties: IEventTypeOption[] = [];

    di.item.properties.forEach((property) => {
      const propertyDefinition = this.getPropertyDefinition(di.item, property, this.props.sd.project);

      if (property.selectedType === EPropertyType.event && propertyDefinition) {
        eventProperties.push({
          label: propertyDefinition.tagName || property.name,
          value: property.name
        });
      }
    });

    if (eventProperties.length === 0) {
      return [
        { label: "onChange", value: "onChange" },
        { label: "onClick", value: "onClick" }
      ];
    }

    return eventProperties;
  };

  renderEventType = (): JSX.Element => {
    const triggerEventsParam = this.props.parameters.find((element) => element.name === "triggerEvents");
    const eventTypeParam = this.props.parameters.find((element) => element.name === "eventType");

    if (!triggerEventsParam?.symbol?.fixedValue || !eventTypeParam) return <></>;

    const eventTypes: IEventTypeOption[] = this.getEventPropertiesFromComponent();
    const eventTypeOptions: KuikaSelectboxOption[] = eventTypes.map(
      (eventType: IEventTypeOption): KuikaSelectboxOption => ({
        key: eventType.value,
        value: eventType.value,
        label: eventType.label
      })
    );

    return (
      <div style={{ width: "100%", marginTop: "12px" }}>
        <div className="kuika_add_actions_properties_name">Event Type</div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            size="sm"
            style={{ width: "100%" }}
            value={eventTypeParam?.symbol?.fixedValue ?? null}
            onChange={(value: string): void => {
              this.handleSelectSymbolPickerValue(eventTypeParam!, {
                ...eventTypeParam.symbol,
                category: ESymbolCategory.fixed,
                fixedValue: value,
                targetType: "String"
              });
            }}
            className="kuika_add_actions_inputs_size"
            options={eventTypeOptions}
          />
        </div>
      </div>
    );
  };

  getAllDisplayItemsOfDi = (di: IDisplayItem, result: IDisplayItem[]) => {
    if (di.children)
      for (let i = 0; i < di.children.length; i++) {
        result.push(di.children[i]);
        this.getAllDisplayItemsOfDi(di.children[i], result);
      }
  };

  fillAllDisplayItemsRecursive = (sd: IDesign, result: IDisplayItem[]) => {
    if (sd.displayItems)
      for (let i = 0; i < sd.displayItems.length; i++) {
        result.push(sd.displayItems[i]);
        this.getAllDisplayItemsOfDi(sd.displayItems[i], result);
      }
  };

  render() {
    return (
      <div style={{ width: "100%" }}>
        {this.renderComponentList()}
        {this.renderSymbolPicker()}
        {this.renderTriggerEvents()}
        {this.renderEventType()}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const setValueSymbolsGroup = withContext<ISetValueSymbolsGroupProps>(
  withSD<ISetValueSymbolsGroupProps>(SetValueSymbolsGroup)
);
export { setValueSymbolsGroup as SetValueSymbolsGroup };
